import {
  ITO,
  type GetCommentsCommandOutput,
  type ITOClientConfig,
  type SubmitOrderCommandInput,
  type SubmitOrderItem,
} from "@amzn/ito-client";
import { SentryFetchHttpHandler } from "@amzn/sentry-fetch-http-handler";
import { API_BASE_URL } from "@/config";
import { CsrfService } from "@/services/csrf/csrf-service";
import { Order } from "@/pages/Orders/orders.types";
import type { CreateOrderRequest, CreateOrderResponse } from "./orders.types";

const ITO_PROXY_PATH = "/ito-amznham";

export interface GetOrdersResponse {
  orders?: Order[];
  totalResults?: number;
  pageSize?: number;
  nextToken?: string;
}

/**
 * Service for hardware orders. Handles fetching orders and order creation
 * via the ITO ordering API. The /ito-amznham proxy handles SigV4 signing —
 * the browser only needs a valid Midway session and a CSRF token.
 */
export class OrdersService {
  private readonly client: ITO;
  private readonly requestHandler: SentryFetchHttpHandler;
  private readonly csrfService: CsrfService;

  constructor(csrfService: CsrfService = new CsrfService()) {
    this.csrfService = csrfService;
    this.requestHandler = new SentryFetchHttpHandler();
    this.client = new ITO({
      endpoint: API_BASE_URL + ITO_PROXY_PATH,
      region: "*",
      credentials: { accessKeyId: "", secretAccessKey: "" },
      requestHandler: this.requestHandler,
    } as ITOClientConfig);
  }

  public async getMyOrders(pageSize = 20): Promise<GetOrdersResponse> {
    const response = await this.client.getOrders_My({ pageSize });
    return {
      orders: response.orders as unknown as Order[],
      totalResults: response.totalResults,
      pageSize: response.pageSize,
      nextToken: response.nextToken,
    };
  }

  public async createOrder(request: CreateOrderRequest): Promise<CreateOrderResponse> {
    const csrfToken = await this.csrfService.getToken();
    this.attachCsrfToken(csrfToken);

    const items: Record<string, SubmitOrderItem> = {};
    for (const [taxonomyId, item] of Object.entries(request.items)) {
      items[taxonomyId] = {
        quantity: item.quantity,
        details: item.details,
      };
    }

    const order = await this.client.submitOrder({
      items,
      details: request.details,
      deliveryTo: {
        fullName: request.deliveryTo.fullName,
        addressLineData: [
          request.deliveryTo.addressLine1,
          request.deliveryTo.addressLine2,
          request.deliveryTo.addressLine3,
        ].filter(Boolean),
        country: request.deliveryTo.country,
        region: "-",
        county: request.deliveryTo.county || undefined,
        city: request.deliveryTo.city,
        citySubdivision1: request.deliveryTo.citySubdivision1,
        postalCode: request.deliveryTo.postalCode,
        phoneNumber: request.deliveryTo.phoneNumber.trim(),
        deliveryInstructions: request.deliveryTo.deliveryInstructions,
      },
      acknowledgementAudit: request.acknowledgementAudit,
      labels: request.labels,
    } as SubmitOrderCommandInput);

    return {
      orderId: order.orderId ?? "",
      orderReference: order.orderReference ?? "",
      canBeAutoApproved: !order.approver,
    };
  }

  public async getComments(taxonomyId: string, pageSize = 100): Promise<GetCommentsCommandOutput> {
    return this.client.getComments({ taxonomyId, pageSize });
  }

  public async postComment(params: {
    taxonomyId: string;
    message: string;
    csrfToken: string;
    tags: string[];
  }): Promise<{ success: boolean }> {
    this.attachCsrfToken(params.csrfToken);
    await this.client.postComment({
      taxonomyId: params.taxonomyId,
      comment: params.message,
      tags: params.tags,
    });
    return { success: true };
  }

  private attachCsrfToken(csrfToken: string): void {
    this.client.middlewareStack.add(
      (next) => async (args) => {
        (args.request as { headers: Record<string, string> }).headers["x-csrf-token"] = csrfToken;
        return next(args);
      },
      { step: "build", name: "csrfTokenMiddleware", override: true }
    );
  }
}
